import { useEffect, useRef, useState } from 'react';
import { Video, VideoOff, AlertCircle } from 'lucide-react';

interface CameraPreviewProps {
  className?: string;
}

export default function CameraPreview({ className }: CameraPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const startCamera = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      });

      streamRef.current = stream;
      setHasPermission(true);

      // Attach stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      setIsLoading(false);
    } catch (err: any) {
      console.error('Camera access error:', err);
      setHasPermission(false);
      setIsLoading(false);

      // Handle different error types
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera access denied. Please allow camera permissions.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is already in use by another application.');
      } else {
        setError('Failed to access camera. Please check your browser settings.');
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const toggleCamera = () => {
    if (isEnabled) {
      stopCamera();
      setIsEnabled(false);
    } else {
      startCamera();
      setIsEnabled(true);
    }
  };

  // Start camera on mount
  useEffect(() => {
    startCamera();

    // Cleanup on unmount
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className={`relative w-full h-full bg-gray-900 ${className}`}>
      {/* Video Element */}
      {isEnabled && !error && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover transform scale-x-[-1]"
        />
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-400 text-sm">Starting camera...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
          <div className="text-center px-4">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            {hasPermission === false && (
              <button
                onClick={startCamera}
                className="btn-primary text-sm"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      )}

      {/* Camera Off State */}
      {!isEnabled && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <div className="text-center">
            <VideoOff className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Camera is off</p>
          </div>
        </div>
      )}

      {/* Camera Toggle Button */}
      <button
        onClick={toggleCamera}
        className="absolute top-4 right-4 p-3 rounded-full bg-black/50 hover:bg-black/70 transition-colors backdrop-blur-sm border border-white/10"
        title={isEnabled ? 'Turn camera off' : 'Turn camera on'}
      >
        {isEnabled ? (
          <Video className="w-5 h-5 text-white" />
        ) : (
          <VideoOff className="w-5 h-5 text-white" />
        )}
      </button>

      {/* Camera Label */}
      <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/10">
        <p className="text-white text-xs font-medium">Your Camera</p>
      </div>
    </div>
  );
}

